import React, { useCallback, useState } from "react";
import { Control } from "react-redux-form";
import { Dialog, DialogProps } from "../../layout";
import { Icon, Icomoon } from "../../brand";
import { SecondaryButton, LocalForm, TextInputLengthInfo, FieldError } from "../../forms";
import "./EnquiryArchiveDialog.scss";

interface Props extends Pick<DialogProps, "isOpen" | "onClose"> {
  /**
   * Callback for when a reason for archiving is chosen.
   */
  onArchiveReasonSelect: (reason: string) => void;
  
  /**
   * Optional callback for when the back CTA is clicked. If not defined `onClose` will be called.
   */
  onBackClick?: () => void;
}

const formatReason = (reason = "") => {
  return reason.toLocaleLowerCase();
}

/**
 * Component to prompt the LTO for a reason for archiving the enquiry. See #3178
 * 
 * @status development
 */
export const EnquiryArchiveDialog: React.FC<Props> = (props) => {

  const ReasonButton: React.FC<{ reason: string }> = useCallback(({ reason = "Unknown" }) => (
    <button
      type="button"
      className="enquiry-archive-dialog__button"
      onClick={() => props.onArchiveReasonSelect(formatReason(reason))}
    >
      {reason}
    </button>
  ), [props.onArchiveReasonSelect]);

  const [isOtherOpen, setIsOtherOpen] = useState(false);

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      ariaLabel="Dialog for choosing a reason for archiving this enquiry"
      className="enquiry-archive-dialog"
      hasCloseButton
    >
      <div className="columns">
        <div className="column is-5 is-4-desktop enquiry-archive-dialog__info is-flex">
          <Icon className="enquiry-archive-dialog__icon" icomoon={Icomoon.drawer3} />
          <h1 className="heading--2">Archive this enquiry</h1>
          <p>Select the option that best describes the reason why you want to do this.</p>

          <SecondaryButton
            className="is-paddingless enquiry-archive-dialog__cancel"
            onClick={props.onBackClick ?? props.onClose}
          >
            Back
          </SecondaryButton>
        </div>

        <div className="column">
          <div className="columns is-multiline">
            <div className="column is-half">
              <ReasonButton reason="Booked with competitor" />
            </div>
            <div className="column is-half">
              <ReasonButton reason="Too expensive" />
            </div>
            <div className="column is-half">
              <ReasonButton reason="No longer able to go" />
            </div>
            <div className="column is-half">
              <ReasonButton reason="Itinerary not suitable" />
            </div>
            <div className="column is-half">
              <ReasonButton reason="Unhappy with service" />
            </div>
            <div className="column is-half">
              <ReasonButton reason="Opted to self-book" />
            </div>
            <div className="column is-half">
              <ReasonButton reason="No reply after 3 follow ups" />
            </div>
            <div className="column is-half">
              <button
                type="button"
                className="enquiry-archive-dialog__button"
                onClick={() => setIsOtherOpen(true)}
              >
                Other...
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        isOpen={isOtherOpen}
        onClose={() => setIsOtherOpen(false)}
        title="Reason for archiving"
        ariaLabel="Dialog to provide a custom reason for archiving an enquiry"
        className="enquiry-archive-dialog__other"
        closeOnClickOutside
        hasCloseButton
      >
        <LocalForm
          initialState={{ reason: "" }}
          onSubmit={({ reason }) => {
            props.onArchiveReasonSelect(formatReason(reason));
            setIsOtherOpen(false);
          }}
          className="tl-form">
          {data => (
            <>
              <TextInputLengthInfo value={data.reason} maxLength={30} />
              <Control.text model=".reason" placeholder="Type here..." maxLength={30} validators={{
                length: (val: string) => (val?.length ?? 0) >= 5,
              }} />
              <FieldError model=".reason" messages={{
                length: "Please type a longer reason"
              }} />

              <div className="level is-mobile">
                <div className="level-left">
                  <div className="level-item">
                    <SecondaryButton className="is-paddingless is-marginless" onClick={() => setIsOtherOpen(false)}>
                      Back
                    </SecondaryButton>
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <SecondaryButton type="submit" className="is-paddingless is-marginless">
                      Submit
                    </SecondaryButton>
                  </div>
                </div>
              </div>
            </>
          )}
        </LocalForm>
      </Dialog>
    </Dialog>
  );
};
